import { DownloadOutlined } from '@ant-design/icons';
import { Button, Image, Popover, QRCode, Space, Spin } from 'antd';
import QR from 'qrcode';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

export interface QRCodeViewerProps {
  value: string;
  size?: number;
  mode?: 'image' | 'popover';
  bordered?: boolean;
  icon?: string;
  iconSize?: number;
  previewSize?: number;
  description?: React.ReactNode;
  fileName?: string;
}

/**
 * QRCodeViewer
 * - mode="popover": shows small QR; hover shows larger QR (AntD QRCode)
 * - mode="image": shows an Image with QR dataURL; click opens Image preview (bigger)
 */
const QRCodeViewer: React.FC<QRCodeViewerProps> = ({
  value,
  size = 64,
  mode = 'image',
  bordered = false,
  description = null,
  icon,
  iconSize,
  previewSize = 300,
  fileName = 'qr-code.png',
}) => {
  const [dataUrl, setDataUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const qrOptions = useMemo(
    () => ({
      width: previewSize,
      margin: bordered ? 2 : 0,
      errorCorrectionLevel: 'M' as const,
    }),
    [previewSize, bordered],
  );

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!value) {
        setDataUrl('');
        return;
      }

      setLoading(true);
      try {
        const url = await QR.toDataURL(value, qrOptions);
        if (!cancelled) setDataUrl(url);
      } catch (e) {
        if (!cancelled) setDataUrl('');
        // eslint-disable-next-line no-console
        console.error('QRCodeViewer: failed to generate QR dataURL', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [value, qrOptions]);

  const handleDownload = useCallback(() => {
    if (!dataUrl) return;

    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [dataUrl, fileName]);

  const smallQRCode = (
    <QRCode
      value={value}
      size={size}
      bordered={bordered}
      icon={icon}
      iconSize={iconSize}
    />
  );

  if (mode === 'popover') {
    return (
      <Popover
        placement="right"
        trigger="hover"
        content={
          <Space direction="vertical" align="center">
            <QRCode
              value={value}
              size={200}
              bordered={bordered}
              icon={icon}
              iconSize={iconSize}
            />
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              disabled={!dataUrl}
              block
            >
              Download
            </Button>
          </Space>
        }
      >
        <div style={{ cursor: 'pointer', display: 'inline-flex' }}>
          {smallQRCode}
        </div>
      </Popover>
    );
  }

  return (
    <Space direction="vertical" align={"center"}>
      <Image
        width={size}
        src={dataUrl || undefined}
        fallback=""
        style={{ cursor: 'pointer' }}
        placeholder={loading ? <Spin /> : undefined}
        preview={{
          src: dataUrl || undefined,
          mask: null,
          imageRender: (node) => (
            <Space direction="vertical" align="center">
              {node}
              {description}
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                disabled={!dataUrl}
              >
                Download
              </Button>
            </Space>
          ),
        }}
      />
      <Button
        size={"small"}
        type={"text"}
        icon={<DownloadOutlined />}
        onClick={handleDownload}
        disabled={!dataUrl}
        block
      >
        Download
      </Button>
    </Space>
  );
};

export default QRCodeViewer;
